<template>
  <section class="relative mt-12 sm:mt-20 xl:mt-24">
    <div class="_unmargin-bottom offer-selection-filter text-white">
      <Container v-show="offerSelection">
        <div ref="filterElement" class="mx-auto max-w-[55rem]">
          <h2 class="title mb-5">{{ offerSelection.heading }}</h2>
          <p class="lead">{{ offerSelection.subtitle }}</p>

          <ol>
            <!-- STEP 1 COMPANY PHASES -->
            <li v-if="companyPhases" class="mt-12">
              <h3
                class="mb-8 text-xl font-semibold leading-[1.45rem] tracking-normal lg:text-2xl xl:!text-[1.75rem]"
              >
                1. {{ offerSelection.question_1 }}
              </h3>
              <ul class="flex flex-col justify-between gap-5 md:flex-row">
                <li v-for="phase in companyPhases" :key="phase.id">
                  <button
                    class="group flex h-auto w-full items-center rounded border-2 border-white p-4 transition-all hover:bg-white md:block md:h-48 md:w-52 lg:h-56 lg:w-[17rem]"
                    :class="{
                      'bg-white':
                        chosenOfferFilter &&
                        chosenOfferFilter.companyPhase === phase.slug,
                    }"
                    @click="setFilter('companyPhase', phase.slug)"
                  >
                    <img
                      v-if="phase.icon"
                      :src="phase.icon.permalink"
                      :alt="phase.icon.alt"
                      class="group-hover:primary-blue-filter mr-6 h-9 w-9 shrink-0 md:mb-5 md:mr-0 md:h-16 md:w-16 lg:h-24 lg:w-24"
                      :class="
                        chosenOfferFilter.companyPhase === phase.slug
                          ? 'primary-blue-filter'
                          : 'invert'
                      "
                    />
                    <p
                      class="text-base font-semibold uppercase tracking-widest group-hover:text-primary-blue"
                      :class="{
                        'text-primary-blue':
                          chosenOfferFilter.companyPhase === phase.slug,
                      }"
                    >
                      {{ phase.title }}
                    </p>
                  </button>
                </li>
              </ul>
            </li>

            <!-- STEP 2 OFFER TOPICS -->
            <Accordion
              v-if="offerTopics"
              :open="!!chosenOfferFilter.companyPhase"
            >
              <template #content>
                <li class="mt-12">
                  <h3
                    class="mb-8 text-xl font-semibold leading-[1.45rem] tracking-normal lg:text-2xl xl:!text-[1.75rem]"
                  >
                    2. {{ offerSelection.question_2 }}
                  </h3>
                  <ul class="flex flex-wrap">
                    <li v-for="topic in offerTopics" :key="topic.id">
                      <button
                        class="mb-3 mr-3 flex h-fit min-h-[2rem] items-center rounded-[1.5em] border-2 border-white px-3 py-2 text-left text-xl font-medium text-white transition-colors hover:bg-white hover:text-primary-blue sm:min-h-[3rem] sm:px-4"
                        :class="{
                          'bg-white !text-primary-blue':
                            chosenOfferFilter.offerTopic === topic.slug,
                        }"
                        @click="setFilter('offerTopic', topic.slug)"
                      >
                        {{ topic.title }}
                      </button>
                    </li>
                    <li>
                      <button
                        class="mb-3 mr-3 flex h-fit min-h-[2rem] items-center rounded-[1.5em] border-2 border-white px-3 py-2 text-left text-xl font-medium text-white transition-colors hover:bg-white hover:text-primary-blue sm:min-h-[3rem] sm:px-4"
                        :class="{
                          'bg-white !text-primary-blue':
                            chosenOfferFilter.offerTopic === 'all',
                        }"
                        @click="setFilter('offerTopic', 'all')"
                      >
                        {{ translations.whole_amount }}
                      </button>
                    </li>
                  </ul>
                </li>
              </template>
            </Accordion>

            <!-- STEP 3 OFFER LANGUAGES -->
            <Accordion
              v-if="offerLanguages"
              :open="
                !!chosenOfferFilter.companyPhase &&
                !!chosenOfferFilter.offerTopic
              "
            >
              <template #content>
                <li class="mt-12">
                  <h3
                    class="mb-8 text-xl font-semibold leading-[1.45rem] tracking-normal lg:text-2xl xl:!text-[1.75rem]"
                  >
                    3. {{ offerSelection.question_3 }}
                  </h3>
                  <ul class="flex flex-wrap">
                    <li v-for="language in offerLanguages" :key="language.id">
                      <button
                        class="mb-3 mr-3 flex h-fit min-h-[2rem] items-center rounded-[2em] border-2 border-white px-3 py-2 text-left text-xl font-medium text-white transition-colors hover:bg-white hover:text-primary-blue sm:min-h-[3rem] sm:px-4"
                        :class="{
                          'bg-white !text-primary-blue':
                            chosenOfferFilter.offerLanguage === language.slug,
                        }"
                        @click="setFilter('offerLanguage', language.slug)"
                      >
                        {{ language.title }}
                      </button>
                    </li>
                  </ul>
                </li>
              </template>
            </Accordion>
          </ol>
        </div>
      </Container>
    </div>

    <!-- FILTER   -->
    <Container v-show="readyToFetch">
      <div class="offer-selection-results mx-auto my-8 max-w-75rem md:my-14">
        <div class="flex flex-wrap items-center justify-between gap-7 sm:gap-9">
          <div class="flex flex-wrap items-center gap-7 sm:gap-9">
            <!-- BUTTON -->
            <button
              class="flex items-center space-x-3 rounded-[1.75em] border border-primary-blue bg-primary-blue px-4 py-3 text-base uppercase text-white transition-colors hover:bg-white hover:text-primary-blue"
              @click="scrollTo()"
            >
              <FilterIcon class="h5 w-5" />
              <span class="font-semibold">{{ offerSelection.filter }}</span>
            </button>

            <!-- PARTNER DROPDOWN -->
            <div v-if="partners" class="relative">
              <select
                v-model="selectedPartner"
                class="w-fit max-w-[89vw] cursor-pointer appearance-none overflow-hidden text-ellipsis whitespace-pre !pr-12 text-xl font-medium text-primary-blue outline-none focus:ring-0"
                :style="`width: ${
                  selectedPartner?.title?.length + 3 ||
                  offerSelection.partner_dropdown.length + 3
                }ch`"
              >
                <option value="">{{ offerSelection.partner_dropdown }}</option>
                <option
                  v-for="partner in partners"
                  :key="partner.id"
                  :value="partner"
                >
                  {{ partner.title }}
                </option>
              </select>
              <ArrowSimpleIcon
                stroke-width="2.5"
                class="pointer-events-none absolute right-4 top-1/2 w-4 -translate-y-1/2 text-primary-blue transition-transform"
              />
            </div>
          </div>
          <div class="space-x-2 text-black">
            <span class="font-bold">{{ filteredOffers.length }}</span>
            <span>
              {{
                filteredOffers.length === 1
                  ? offerSelection.result_singular
                  : offerSelection.results
              }}
            </span>
          </div>
        </div>
      </div>
    </Container>

    <!-- RESULTS -->
    <transition name="fade">
      <div
        v-if="Array.isArray(filteredOffers) && filteredOffers.length > 0"
        class="w-full bg-primary-grey-light"
      >
        <Container class="mx-auto max-w-[75rem] py-8 sm:py-14">
          <ul
            class="relative grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3"
          >
            <!-- OFFER CARDS -->

            <li v-for="offer in filteredOffers" :key="offer.id" class="flex">
              <button
                class="card-shadow group inset-0 flex w-full max-w-[34rem] cursor-pointer flex-col justify-between bg-white transition-all duration-700 hover:-translate-y-3 hover:duration-300 xl:max-w-[24rem]"
                @click="selectedOffer = offer"
              >
                <!-- HEADER -->
                <div>
                  <div
                    class="w-full px-6 pb-7 pt-8 text-left text-xl font-medium text-primary-blue sm:text-2xl"
                  >
                    {{ offer.title }}
                  </div>

                  <!-- BODY -->
                  <div class="p-6 pt-0 text-left text-xl">
                    {{ offer.short_description }}
                  </div>
                </div>

                <div class="flex w-full items-end justify-between p-6 pt-0">
                  <!-- FOOTER -->
                  <div class="max-w-[45%] shrink-0">
                    <img
                      v-if="offer.partner?.logo"
                      :src="offer.partner.logo.permalink"
                      :alt="offer.partner.logo.alt"
                      class="h-full max-h-[4rem] grayscale"
                    />
                  </div>
                  <div class="p-3">
                    <PlusIcon
                      class="w-4 text-primary-blue transition-transform duration-700 group-hover:rotate-180 group-hover:duration-300"
                    />
                  </div>
                </div>
              </button>
            </li>
          </ul>
        </Container>
      </div>
    </transition>

    <transition name="fade">
      <div v-if="loading" class="flex w-full justify-center">
        <SpinIcon
          class="mx-auto h-10 w-10 animate-spin text-primary-blue-gray"
        />
      </div>
    </transition>

    <transition name="fade">
      <div
        v-if="
          readyToFetch &&
          !loading &&
          Array.isArray(filteredOffers) &&
          filteredOffers.length === 0
        "
        key="no-elements-found"
        class="w-full"
      >
        <p class="my-10 text-center text-xl">{{ translations.filter_empty }}</p>
      </div>
    </transition>

    <transition name="fade">
      <div
        v-if="selectedOffer"
        class="fixed inset-0 z-20 h-screen w-screen overflow-auto bg-black bg-opacity-20"
        @click="selectedOffer = null"
    /></transition>

    <transition name="slide-right-in">
      <OfferSidebar
        v-if="selectedOffer"
        :data="selectedOffer"
        @close="selectedOffer = null"
      />
    </transition>
  </section>
</template>

<script setup>
import { store } from '/store.js'
import { useUrlSearchParams } from '@vueuse/core'
const { locale } = useI18n()

const route = useRoute()

// PROPS
const props = defineProps({
  data: { type: Object, default: () => ({}) },
})

// VARIABLES
const params = useUrlSearchParams('history')
const translations = computed(() => store.translations)
const offerSelection = computed(() => store.offerSelection)
const loading = ref(false)

const offers = ref([])

const selectedOffer = ref(null)
const filterElement = ref(null)

// const chosenOfferFilter = ref(
//   route.query.chosenOfferFilter ? JSON.parse(route.query.chosenOfferFilter) : {}
// )
const chosenOfferFilter = ref({})

const companyPhases = ref(null)
const offerTopics = ref(null)
const offerLanguages = ref(null)

// const selectedPartner = ref(
//   route.query.offerSelectedPartner ? JSON.parse(route.query.offerSelectedPartner) : ''
// )
const selectedPartner = ref(null)

// FUNCTIONS
async function fetchOffers() {
  await nextTick()
  if (!readyToFetch.value) {
    return
  }

  loading.value = true

  const { data } = await useAsyncGql({
    operation: 'Offers',
    variables: {
      sort: 'title asc',
      filter: {
        site: locale,
        company_phases: {
          contains: chosenOfferFilter.value.companyPhase,
        },
        offer_topics: {
          contains:
            chosenOfferFilter.value.offerTopic === 'all'
              ? ''
              : chosenOfferFilter.value.offerTopic,
        },
        offer_language: {
          contains: chosenOfferFilter.value.offerLanguage,
        },
      },
    },
  })

  loading.value = false
  const payload = data.value?.entries

  if (!payload) {
    console.error('Offers could not be fetched')
    return
  }

  // totalPages.value = Math.ceil(payload.total / payload.per_page)
  // currentPage.value = payload.current_page

  offers.value = payload.data
}

function setFilter(field, id) {
  chosenOfferFilter.value[field] = id
  fetchOffers()
  // params.chosenOfferFilter = JSON.stringify(chosenOfferFilter.value)

  // revert selected partner
  selectedPartner.value = ''
  // params.offerSelectedPartner = ''
}

function scrollTo() {
  filterElement.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

// COMPUTED
const readyToFetch = computed(() => {
  return Object.keys(chosenOfferFilter.value).length === 3
})

const partners = computed(() => {
  if (!Array.isArray(offers.value) || offers.value.length === 0) {
    return []
  }
  let partners = []

  offers.value.forEach((offer) => {
    if (
      offer.partner &&
      !partners.some((partner) => partner.id === offer.partner.id)
    ) {
      partners.push(offer.partner)
    }
  })

  return partners.sort((a, b) => {
    const aTitle = a.title.toLowerCase()
    const bTitle = b.title.toLowerCase()
    if (aTitle < bTitle) {
      return -1
    }
    if (aTitle > bTitle) {
      return 1
    }
    return 0
  })
})

const filteredOffers = computed(() => {
  if (!Array.isArray(offers.value) || offers.value.length === 0) {
    return []
  }
  let filteredOffers = offers.value

  if (selectedPartner.value) {
    filteredOffers = offers.value.filter((offer) => {
      return offer.partner?.id === selectedPartner.value.id
    })
  }

  return filteredOffers
})

// watch(selectedPartner, (newValue) => {
//   params.offerSelectedPartner = JSON.stringify(newValue)
// })

// INITIAL
const response = await useAsyncGql({
  operation: 'OfferTerms',
  variables: {
    filter: {
      site: locale,
    },
  },
})

companyPhases.value = response?.data.value?.company_phases?.data
offerTopics.value = response?.data.value?.offer_topics?.data
offerLanguages.value = response?.data.value?.offer_language?.data

// Use it if the filter need to be persistent as url params but mind the node missmatches
// if (Object.keys(chosenOfferFilter.value).length === 3) {
//   const { data } = await useAsyncGql({
//     operation: 'Offers',
//     variables: {
//       sort: 'title asc',
//       filter: {
//         site: locale,
//         company_phases: {
//           contains: chosenOfferFilter.value.companyPhase,
//         },
//         offer_topics: {
//           contains:
//             chosenOfferFilter.value.offerTopic === 'all'
//               ? ''
//               : chosenOfferFilter.value.offerTopic,
//         },
//         offer_language: {
//           contains: chosenOfferFilter.value.offerLanguage,
//         },
//       },
//     },
//   })
//   const payload = data.value?.entries

//   if (!payload) {
//     console.error('Offers could not be fetched')
//   }

//   offers.value = payload.data
// }

onMounted(() => {
  chosenOfferFilter.value.offerTopic = props.data.preselected_offer_topic?.slug
})
</script>

<style lang="scss" scoped>
.card-shadow {
  box-shadow: 0 4px 8px 1px rgb(0 0 0 / 16%);
}
.card-shadow:hover {
  box-shadow: 0 4px 8px 2px rgba(0, 0, 0, 0.25);
}
</style>
